import { useEffect, useMemo, useRef, useState } from 'react';
import { elementColor } from '../elements';
import { resolveElement } from '../atomic/atomicData';
import {
  VizCard,
  NowYouTry,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  DARK_INK,
  VIZ_BTN_GHOST,
  type VizCue,
} from '../vizShared';

// BondingContinuum — the bonding "spectrum" as the hero: a single horizontal
// ΔEN axis split into three zones (nonpolar covalent 0–0.4 · polar covalent
// 0.4–1.7 · ionic ≥1.8), a marker dropped at the chosen pair's ΔEN, and BELOW it
// an electron-picture that MORPHS to match the zone — equal shared pair (covalent)
// → shared pair pulled toward the more-electronegative atom with δ± (polar) →
// full electron transfer to a bracketed cation + anion (ionic). Where the sibling
// BondPolarity widget focuses on the dipole arrow, this one teaches the MECHANISM:
// what the electrons actually do as ΔEN grows. Resilient default: H–Cl.
//
// EN values come from ../atomic/atomicData (curated Pauling scale, single source
// of truth) via resolveElement, so we never keep a private copy.
//
// Self-demo: on mount the marker sweeps 0 → the pair's true ΔEN, the zone label
// and the electron-picture animate along with it, then "Now you try".
//
// cue: { progress? → scrub the marker 0..1 across the axis (0..MAX);
//        step? / highlight? → select an example pair; the picture follows }
//
// config: { a?, b?, title? }  (a/b are element symbols; default H / Cl)

export interface BondingContinuumConfig {
  title?: string;
  a?: string; // element symbol
  b?: string; // element symbol
  atoms?: string[]; // alias for [a, b] so {atoms:["Na","Cl"]} also works
}

interface Props {
  config?: BondingContinuumConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// Pauling EN for a symbol, via the shared dataset. null ⇒ not defined (treated 0).
function enOf(symbol: string): number | null {
  return resolveElement(symbol)?.electronegativity ?? null;
}

// Common teaching thresholds (matches the sibling BondPolarity widget). The
// "grey zone" 1.7–1.8 is where courses disagree; we label ≥1.8 ionic, <1.7 polar.
const T_NONPOLAR = 0.4;
const T_IONIC = 1.8;
const MAX = 3.3; // ΔEN axis maximum (covers Cs–F ≈ 3.19)

type ZoneKey = 'nonpolar' | 'polar' | 'ionic';

function classify(d: number): { key: ZoneKey; label: string; color: string; blurb: string } {
  if (d < T_NONPOLAR)
    return {
      key: 'nonpolar',
      label: 'Nonpolar covalent',
      color: 'var(--ws-success)',
      blurb: 'Electrons shared (almost) equally.',
    };
  if (d < T_IONIC)
    return {
      key: 'polar',
      label: 'Polar covalent',
      color: 'var(--ws-warning)',
      blurb: 'Shared pair pulled toward the more electronegative atom.',
    };
  return {
    key: 'ionic',
    label: 'Ionic',
    color: 'var(--ws-danger)',
    blurb: 'Electron transferred — a cation and an anion form.',
  };
}

// Example pairs spanning the whole continuum (left→right), for cue.step / chips.
const EXAMPLES: Array<{ label: string; a: string; b: string }> = [
  { label: 'Cl–Cl', a: 'Cl', b: 'Cl' },
  { label: 'H–H', a: 'H', b: 'H' },
  { label: 'H–Cl', a: 'H', b: 'Cl' },
  { label: 'O–H', a: 'O', b: 'H' },
  { label: 'Na–Cl', a: 'Na', b: 'Cl' },
  { label: 'K–F', a: 'K', b: 'F' },
];

// ── Geometry (SVG user units) ────────────────────────────────────────────────
const W = 380;
const SCALE_H = 70; // top: the continuum axis
const PIC_H = 130; // bottom: the electron picture
const H = SCALE_H + PIC_H;
const barX = 26;
const barW = W - 52;

export default function BondingContinuum({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Accept {a,b} or {atoms:[a,b]}; fall back to H–Cl only if a side is unusable.
  const cfgA = config.a ?? config.atoms?.[0];
  const cfgB = config.b ?? config.atoms?.[1];
  const [a, setA] = useState(() => (cfgA && enOf(cfgA) != null ? cfgA : 'H'));
  const [b, setB] = useState(() => (cfgB && enOf(cfgB) != null ? cfgB : 'Cl'));
  const interactedRef = useRef(false);
  const rafRef = useRef<number>(0);

  const enA = enOf(a) ?? 0;
  const enB = enOf(b) ?? 0;
  const trueDelta = useMemo(() => Math.abs(enA - enB), [enA, enB]);

  // Displayed ΔEN: during the self-demo / a progress-cue this can differ from the
  // pair's true value (we sweep it). Selecting a pair re-syncs it.
  const [delta, setDelta] = useState<number>(reduced || hasCue ? trueDelta : 0);
  const [demoDone, setDemoDone] = useState(reduced || hasCue);

  const markInteracted = () => {
    cancelAnimationFrame(rafRef.current);
    if (!demoDone) setDemoDone(true);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'bonding_continuum', a, b, delta: trueDelta });
    }
  };

  // Self-demo: ease the marker 0 → true ΔEN on mount (unless reduced / cue-driven).
  useEffect(() => {
    if (reduced || hasCue) {
      setDelta(trueDelta);
      return;
    }
    const start = performance.now();
    const dur = 1600;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDelta(trueDelta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else setDemoDone(true);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cue handling (supersedes the self-demo) ──
  const highlights = useMemo(() => cueHighlights(cue), [cue]);
  const selectPair = (na: string, nb: string, sync = true) => {
    setA(na);
    setB(nb);
    if (sync) setDelta(Math.abs((enOf(na) ?? 0) - (enOf(nb) ?? 0)));
  };

  // cue.highlight → pick an example by label or by both symbols.
  useEffect(() => {
    if (highlights.length === 0) return;
    const match = EXAMPLES.find(
      (ex) =>
        highlights.includes(ex.label.toLowerCase()) ||
        highlights.includes(ex.label.replace('–', '').toLowerCase()) ||
        (highlights.includes(ex.a.toLowerCase()) && highlights.includes(ex.b.toLowerCase())),
    );
    if (match) selectPair(match.a, match.b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlights]);

  // cue.step → step through example pairs.
  useEffect(() => {
    if (cue?.step == null) return;
    const i = ((Math.round(cue.step) % EXAMPLES.length) + EXAMPLES.length) % EXAMPLES.length;
    selectPair(EXAMPLES[i].a, EXAMPLES[i].b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step]);

  // cue.progress (0..1) → scrub the marker across the axis, but ONLY while
  // playing. At rest (paused) keep the chosen pair's true ΔEN so the slide
  // doesn't snap the marker to 0 (nonpolar).
  useEffect(() => {
    if (cue?.progress == null || cue.play !== true) return;
    setDelta(Math.max(0, Math.min(1, cue.progress)) * MAX);
  }, [cue?.progress, cue?.play]);

  const zone = classify(delta);
  const frac = Math.min(1, delta / MAX);
  const markerX = barX + frac * barW;
  // While scrubbing a synthetic ΔEN, fall back to the atom ordering for the sign.
  const bMoreEN = enB >= enA;
  const moreSym = bMoreEN ? b : a;
  const lessSym = bMoreEN ? a : b;

  const trans = reduced ? undefined : 'all 220ms ease-out';

  return (
    <VizCard
      eyebrow="Bonding · The bonding continuum"
      title={config.title ?? 'From sharing to transfer: the bonding continuum'}
      subtitle="ΔEN slides the bond along one spectrum — covalent → polar → ionic"
      reducedMotion={reduced}
      hint="Pick two atoms or scrub ΔEN · the electrons below follow the zone · examples sweep on load"
    >
      <div className="flex w-full flex-col gap-3">
        {/* Pair pickers + live readout */}
        <div className="flex flex-wrap items-end gap-2">
          <Picker label="Atom A" value={a} onChange={(v) => { selectPair(v, b); markInteracted(); }} />
          <Picker label="Atom B" value={b} onChange={(v) => { selectPair(a, v); markInteracted(); }} />
          <dl className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 rounded-sm border border-ws-border bg-ws-tile px-2 py-1.5 text-xs text-ws-ink-2">
            <div><dt className="inline text-ws-ink-3">EN({a}) </dt><dd className="inline font-medium">{enA.toFixed(2)}</dd></div>
            <div><dt className="inline text-ws-ink-3">EN({b}) </dt><dd className="inline font-medium">{enB.toFixed(2)}</dd></div>
            <div><dt className="inline text-ws-ink-3">ΔEN </dt><dd className="inline font-semibold text-ws-ink">{delta.toFixed(2)}</dd></div>
          </dl>
        </div>

        {/* The continuum + morphing electron picture */}
        <div className="relative">
          <NowYouTry show={demoDone && !hasCue && !reduced} />
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full rounded-md border border-ws-border bg-ws-surface-2"
            role="img"
            aria-label={`ΔEN ${delta.toFixed(2)} — ${zone.label}. ${zone.blurb}`}
          >
            {/* ── The continuum axis (hero) ── */}
            {/* zone bands */}
            <rect x={barX} y={24} width={barW * (T_NONPOLAR / MAX)} height={14} rx={3}
              fill="var(--ws-success)" opacity={zone.key === 'nonpolar' ? 0.95 : 0.45} />
            <rect x={barX + barW * (T_NONPOLAR / MAX)} y={24} width={barW * ((T_IONIC - T_NONPOLAR) / MAX)} height={14}
              fill="var(--ws-warning)" opacity={zone.key === 'polar' ? 0.95 : 0.45} />
            <rect x={barX + barW * (T_IONIC / MAX)} y={24} width={barW * ((MAX - T_IONIC) / MAX)} height={14} rx={3}
              fill="var(--ws-danger)" opacity={zone.key === 'ionic' ? 0.95 : 0.45} />

            {/* zone captions */}
            <text x={barX + barW * (T_NONPOLAR / MAX) / 2} y={16} textAnchor="middle" fontSize={8}
              fill="var(--ws-ink-3)">covalent</text>
            <text x={barX + barW * ((T_NONPOLAR + T_IONIC) / 2 / MAX)} y={16} textAnchor="middle" fontSize={8}
              fill="var(--ws-ink-3)">polar covalent</text>
            <text x={barX + barW * ((T_IONIC + MAX) / 2 / MAX)} y={16} textAnchor="middle" fontSize={8}
              fill="var(--ws-ink-3)">ionic</text>

            {/* tick labels */}
            <text x={barX} y={52} fontSize={9} fill="var(--ws-ink-3)" textAnchor="middle">0</text>
            <text x={barX + barW * (T_NONPOLAR / MAX)} y={52} fontSize={9} fill="var(--ws-ink-3)" textAnchor="middle">0.4</text>
            <text x={barX + barW * (T_IONIC / MAX)} y={52} fontSize={9} fill="var(--ws-ink-3)" textAnchor="middle">1.8</text>
            <text x={barX + barW} y={52} fontSize={9} fill="var(--ws-ink-3)" textAnchor="middle">{MAX}</text>

            {/* marker dropped at ΔEN */}
            <g style={trans ? { transition: 'transform 140ms linear' } : undefined} transform={`translate(${markerX} 0)`}>
              <line x1={0} y1={20} x2={0} y2={42} stroke="var(--ws-ink)" strokeWidth={2} />
              <polygon points="0,20 -5,12 5,12" fill="var(--ws-ink)" />
              <text x={0} y={10} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--ws-ink)">
                {delta.toFixed(2)}
              </text>
            </g>

            {/* divider */}
            <line x1={0} y1={SCALE_H} x2={W} y2={SCALE_H} stroke="var(--ws-border)" strokeWidth={1} />

            {/* ── The morphing electron picture ── */}
            <ElectronPicture
              zone={zone.key}
              moreSym={moreSym}
              lessSym={lessSym}
              reduced={!!reduced}
            />
          </svg>
        </div>

        {/* Zone label + one-line explanation */}
        <div
          className="flex items-center gap-2 rounded-sm border px-3 py-2 text-xs"
          style={{ borderColor: zone.color, transition: trans }}
        >
          <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: zone.color }} />
          <span className="font-semibold" style={{ color: zone.color }}>{zone.label}</span>
          <span className="text-ws-ink-2">— {zone.blurb}</span>
        </div>

        {/* Example pairs */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">Examples</span>
          {EXAMPLES.map((ex) => {
            const active = a === ex.a && b === ex.b;
            return (
              <button
                key={ex.label}
                type="button"
                onClick={() => { selectPair(ex.a, ex.b); markInteracted(); }}
                aria-pressed={active}
                className={`${VIZ_BTN_GHOST} !px-2 !py-1 font-mono ${active ? '!border-ws-accent !bg-ws-accent !text-ws-on-accent' : ''}`}
              >
                {ex.label}
              </button>
            );
          })}
        </div>
      </div>
    </VizCard>
  );
}

// ── The electron picture: drawn in the bottom region (y from SCALE_H..H) ──────
// Renders two atoms and shows what their bonding electrons do for the active
// zone: a centered shared pair (nonpolar), a shared pair pulled toward the more
// electronegative atom with δ± (polar), or full transfer to a bracketed cation +
// anion (ionic).
function ElectronPicture({
  zone,
  moreSym,
  lessSym,
  reduced,
}: {
  zone: ZoneKey;
  moreSym: string; // more electronegative atom (gains / pulls electrons)
  lessSym: string; // less electronegative atom (gives / loses electrons)
  reduced: boolean;
}) {
  const cy = SCALE_H + PIC_H / 2 - 6;
  const r = 22;
  // Ionic pulls the atoms apart; covalent/polar keep them bonded (closer).
  const gap = zone === 'ionic' ? 96 : 64;
  const leftX = W / 2 - gap / 2;
  const rightX = W / 2 + gap / 2;
  // In the picture, place the LESS-EN atom on the left, MORE-EN on the right, so
  // "electrons drift right" reads consistently across the zones.
  const trans = reduced ? undefined : 'all 260ms ease-out';

  // Shared pair position: centered (nonpolar), shifted toward more-EN (polar).
  const pairX = zone === 'polar' ? W / 2 + 14 : W / 2;

  const Atom = ({
    x,
    sym,
    charge,
    bracket,
  }: { x: number; sym: string; charge?: string; bracket?: boolean }) => (
    <g style={trans ? { transition: 'transform 260ms ease-out' } : undefined} transform={`translate(${x} ${cy})`}>
      {bracket && (
        <>
          <path d={`M ${-r - 8} ${-r - 4} h -5 v ${2 * (r + 4)} h 5`} fill="none" stroke="var(--ws-ink-2)" strokeWidth={1.5} />
          <path d={`M ${r + 8} ${-r - 4} h 5 v ${2 * (r + 4)} h -5`} fill="none" stroke="var(--ws-ink-2)" strokeWidth={1.5} />
        </>
      )}
      <circle cx={0} cy={0} r={r} fill={elementColor(sym)} stroke="rgba(0,0,0,0.25)" strokeWidth={1} />
      <text x={0} y={5} textAnchor="middle" fontSize={15} fontWeight={800} style={{ fill: DARK_INK }}>{sym}</text>
      {charge && (
        <text x={r + 16} y={-r + 2} textAnchor="middle" fontSize={13} fontWeight={800} fill="var(--ws-ink)">
          {charge}
        </text>
      )}
    </g>
  );

  return (
    <g>
      {/* heading for the picture region */}
      <text x={W / 2} y={SCALE_H + 18} textAnchor="middle" fontSize={9} fill="var(--ws-ink-3)">
        {zone === 'ionic' ? 'Electron transfer' : 'Shared bonding pair'}
      </text>

      {zone !== 'ionic' && (
        <>
          {/* bond line connecting the two atoms */}
          <line x1={leftX + r} y1={cy} x2={rightX - r} y2={cy} stroke="var(--ws-ink-3)" strokeWidth={3}
            style={trans ? { transition: 'all 260ms ease-out' } : undefined} />
          {/* the shared electron pair (two dots), centered or pulled toward more-EN */}
          <g style={trans ? { transition: 'all 260ms ease-out' } : undefined}>
            <circle cx={pairX - 5} cy={cy - 9} r={3.2} fill="var(--ws-accent)" />
            <circle cx={pairX + 5} cy={cy - 9} r={3.2} fill="var(--ws-accent)" />
          </g>
          {/* δ± labels in the polar zone */}
          {zone === 'polar' && (
            <>
              <text x={leftX} y={cy - r - 8} textAnchor="middle" fontSize={12} fontWeight={700} fill="var(--ws-accent)">δ+</text>
              <text x={rightX} y={cy - r - 8} textAnchor="middle" fontSize={12} fontWeight={700} fill="var(--ws-danger)">δ−</text>
            </>
          )}
          <Atom x={leftX} sym={lessSym} />
          <Atom x={rightX} sym={moreSym} />
        </>
      )}

      {zone === 'ionic' && (
        <>
          {/* transfer arrow: electron moves from the less-EN to the more-EN atom */}
          <g stroke="var(--ws-accent)" fill="var(--ws-accent)" strokeWidth={2}
            style={trans ? { transition: 'all 260ms ease-out' } : undefined}>
            <line x1={leftX + r + 4} y1={cy - r - 10} x2={rightX - r - 8} y2={cy - r - 10} />
            <polygon points={`${rightX - r - 8},${cy - r - 10} ${rightX - r - 16},${cy - r - 14} ${rightX - r - 16},${cy - r - 6}`} stroke="none" />
            <circle cx={rightX - r - 8} cy={cy - r - 10} r={3.4} stroke="none" />
          </g>
          {/* cation (lost an electron, +) and anion (gained one, − with an extra dot) */}
          <Atom x={leftX} sym={lessSym} charge="+" bracket />
          <g>
            <Atom x={rightX} sym={moreSym} charge="−" bracket />
            {/* the transferred electron now sits on the anion */}
            <circle cx={rightX} cy={cy - r + 6} r={3.4} fill="var(--ws-accent)" />
          </g>
        </>
      )}
    </g>
  );
}

function Picker({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="min-w-[88px]">
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-ws-border bg-ws-tile px-2 py-1.5 text-sm font-medium text-ws-ink transition-colors hover:border-ws-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
      >
        {EN_OPTIONS.map((sym) => (
          <option key={sym} value={sym}>{sym} ({(enOf(sym) ?? 0).toFixed(2)})</option>
        ))}
      </select>
    </div>
  );
}

// Symbols offered in the pickers: every element that has a defined Pauling EN in
// the shared dataset (Z 1–54 curated), ordered by Z. Computed once at module load.
const EN_OPTIONS: string[] = (() => {
  const syms: Array<{ sym: string; z: number }> = [];
  for (let z = 1; z <= 54; z++) {
    const el = resolveElement(z);
    if (el && el.electronegativity != null) syms.push({ sym: el.symbol, z });
  }
  return syms.sort((p, q) => p.z - q.z).map((s) => s.sym);
})();
